import type { SceneObject } from "../../editor/SceneHost";
import { renderSvgSceneLayer } from "../../editor/render/SvgRenderer";
import type { Bounds } from "../../editor/selectionBounds";
import { unionBounds } from "../../editor/selectionBounds";

const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";
const EXPORT_PADDING = 16;
const DEFAULT_SCALE = 2;
const DEFAULT_BG = "#ffffff";

export type ExportBoardPngOptions = {
  objects: SceneObject[];
  bounds: Bounds;
  assetContentUrl: (assetId: string) => string;
  scale?: number;
  background?: string;
  /** Как у сцены: include для логина, omit для гостя. */
  credentials?: RequestCredentials;
};

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Не удалось отрисовать PNG"));
    img.src = url;
  });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function fetchAsDataUrl(
  src: string,
  credentials: RequestCredentials
): Promise<string | null> {
  if (!src || src.startsWith("data:")) return src || null;
  try {
    const res = await fetch(src, { credentials });
    if (!res.ok) return null;
    return await blobToDataUrl(await res.blob());
  } catch {
    return null;
  }
}

/**
 * Убираем внешние URL и foreignObject до растеризации.
 * foreignObject (стикеры/текст) в SVG→Image травит canvas в Chrome.
 */
async function sanitizeSvgForExport(
  svg: SVGSVGElement,
  credentials: RequestCredentials
): Promise<void> {
  // UI экспорта не нужен
  svg
    .querySelectorAll(
      ".wb-pdf-nav, .wb-pdf-thumbs, .wb-calc-card__keys, .wb-timer-card__controls, .wb-code-card, .wb-audio-card"
    )
    .forEach((el) => el.remove());

  // <canvas> (график калькулятора) → data URL img
  for (const canvas of [...svg.querySelectorAll("canvas")]) {
    try {
      const dataUrl = (canvas as HTMLCanvasElement).toDataURL("image/png");
      const img = document.createElement("img");
      img.src = dataUrl;
      img.alt = "";
      img.style.cssText = (canvas as HTMLCanvasElement).style.cssText;
      canvas.replaceWith(img);
    } catch {
      canvas.remove();
    }
  }

  // HTML <img> → data URL; картинки/PDF → чистый SVG <image>
  const htmlImgs = [...svg.querySelectorAll("img")];
  await Promise.all(
    htmlImgs.map(async (img) => {
      const src = img.getAttribute("src") || (img as HTMLImageElement).src;
      if (!src) {
        img.remove();
        return;
      }
      const dataUrl = src.startsWith("data:") ? src : await fetchAsDataUrl(src, credentials);
      if (!dataUrl) {
        img.remove();
        return;
      }
      img.setAttribute("src", dataUrl);
      (img as HTMLImageElement).src = dataUrl;

      const fo = img.closest("foreignObject");
      const sceneObj = fo?.parentElement;
      if (
        fo &&
        sceneObj &&
        (sceneObj.classList.contains("wb-scene-obj--image") ||
          sceneObj.classList.contains("wb-scene-obj--pdf"))
      ) {
        const w = fo.getAttribute("width") || "0";
        const h = fo.getAttribute("height") || "0";
        const svgImg = document.createElementNS(SVG_NS, "image");
        svgImg.setAttribute("width", w);
        svgImg.setAttribute("height", h);
        svgImg.setAttribute("href", dataUrl);
        svgImg.setAttributeNS(XLINK_NS, "href", dataUrl);
        svgImg.setAttribute("preserveAspectRatio", "xMidYMid meet");
        fo.replaceWith(svgImg);
      }
    })
  );

  // SVG <image href="...">
  for (const el of [...svg.querySelectorAll("image")]) {
    const href =
      el.getAttribute("href") ||
      el.getAttributeNS(XLINK_NS, "href") ||
      el.getAttribute("xlink:href");
    if (!href) {
      el.remove();
      continue;
    }
    if (href.startsWith("data:")) continue;
    const dataUrl = await fetchAsDataUrl(href, credentials);
    if (dataUrl) {
      el.setAttribute("href", dataUrl);
      el.setAttributeNS(XLINK_NS, "href", dataUrl);
    } else {
      el.remove();
    }
  }

  // Все оставшиеся foreignObject → чистый SVG (стикеры/текст/виджеты)
  flattenForeignObjects(svg);
}

function wrapExportLines(text: string, maxWidthPx: number, fontSize: number): string[] {
  const avg = Math.max(6, fontSize * 0.52);
  const maxChars = Math.max(4, Math.floor(maxWidthPx / avg));
  const lines: string[] = [];
  for (const paragraph of text.replace(/\r/g, "").split("\n")) {
    if (!paragraph) {
      lines.push("");
      continue;
    }
    let rest = paragraph;
    while (rest.length > maxChars) {
      let breakAt = rest.lastIndexOf(" ", maxChars);
      if (breakAt < Math.floor(maxChars * 0.4)) breakAt = maxChars;
      lines.push(rest.slice(0, breakAt));
      rest = rest.slice(breakAt).trimStart();
    }
    lines.push(rest);
  }
  return lines.slice(0, 40);
}

function flattenForeignObjects(svg: SVGSVGElement): void {
  for (const fo of [...svg.querySelectorAll("foreignObject")]) {
    const w = Math.max(1, parseFloat(fo.getAttribute("width") || "0") || 1);
    const h = Math.max(1, parseFloat(fo.getAttribute("height") || "0") || 1);
    const parent = fo.parentElement;
    const isSticky = Boolean(parent?.classList.contains("wb-scene-obj--sticky"));
    const isText = Boolean(parent?.classList.contains("wb-scene-obj--text"));
    const div = fo.querySelector("div") as HTMLDivElement | null;

    const frag = document.createDocumentFragment();

    if (isSticky || isText) {
      const bg =
        div?.style.background ||
        (isSticky ? "#fef08a" : "transparent");
      const color = div?.style.color || "#1a1a2e";
      const fontSize = parseFloat(div?.style.fontSize || "") || (isSticky ? 15 : 18);
      const fontFamily = div?.style.fontFamily || "system-ui, sans-serif";
      const fontWeight = div?.style.fontWeight || "normal";
      const fontStyle = div?.style.fontStyle || "normal";
      const textDecoration = div?.style.textDecoration || "none";
      const padX = isSticky ? 12 : 6;
      const padY = isSticky ? 10 : 4;
      const rawText = div?.textContent ?? "";

      if (isSticky || (bg && bg !== "transparent")) {
        const rect = document.createElementNS(SVG_NS, "rect");
        rect.setAttribute("x", "0");
        rect.setAttribute("y", "0");
        rect.setAttribute("width", String(w));
        rect.setAttribute("height", String(h));
        rect.setAttribute("rx", isSticky ? "4" : "0");
        rect.setAttribute("fill", bg === "transparent" ? "#ffffff" : bg);
        if (isSticky) {
          rect.setAttribute("stroke", "rgba(0,0,0,0.06)");
          rect.setAttribute("stroke-width", "1");
        }
        frag.appendChild(rect);
      }

      const textEl = document.createElementNS(SVG_NS, "text");
      textEl.setAttribute("x", String(padX));
      textEl.setAttribute("y", String(padY + fontSize));
      textEl.setAttribute("fill", color);
      textEl.setAttribute("font-size", String(fontSize));
      textEl.setAttribute("font-family", fontFamily);
      if (fontWeight === "bold" || fontWeight === "700" || Number(fontWeight) >= 600) {
        textEl.setAttribute("font-weight", "bold");
      }
      if (fontStyle === "italic") {
        textEl.setAttribute("font-style", "italic");
      }
      if (textDecoration && textDecoration !== "none") {
        textEl.setAttribute("text-decoration", textDecoration);
      }
      textEl.setAttribute("xml:space", "preserve");

      const lineH = fontSize * 1.35;
      const lines = wrapExportLines(rawText, w - padX * 2, fontSize);
      lines.forEach((line, i) => {
        const tspan = document.createElementNS(SVG_NS, "tspan");
        tspan.setAttribute("x", String(padX));
        tspan.setAttribute("dy", i === 0 ? "0" : String(lineH));
        tspan.textContent = line || " ";
        textEl.appendChild(tspan);
      });
      frag.appendChild(textEl);
    } else {
      // Таймер / калькулятор / прочее — простая плашка, без HTML
      const rect = document.createElementNS(SVG_NS, "rect");
      rect.setAttribute("x", "0");
      rect.setAttribute("y", "0");
      rect.setAttribute("width", String(w));
      rect.setAttribute("height", String(h));
      rect.setAttribute("rx", "8");
      rect.setAttribute("fill", "#f8fafc");
      rect.setAttribute("stroke", "#cbd5e1");
      rect.setAttribute("stroke-width", "1");
      frag.appendChild(rect);
      const label = document.createElementNS(SVG_NS, "text");
      label.setAttribute("x", String(w / 2));
      label.setAttribute("y", String(h / 2));
      label.setAttribute("text-anchor", "middle");
      label.setAttribute("dominant-baseline", "middle");
      label.setAttribute("fill", "#64748b");
      label.setAttribute("font-size", "14");
      label.setAttribute("font-family", "system-ui, sans-serif");
      if (parent?.classList.contains("wb-scene-obj--timer")) label.textContent = "Таймер";
      else if (parent?.classList.contains("wb-scene-obj--calculator")) label.textContent = "Калькулятор";
      else if (parent?.classList.contains("wb-scene-obj--code")) label.textContent = "Код";
      else label.textContent = "";
      frag.appendChild(label);
    }

    fo.replaceWith(frag);
  }
}

function filterObjectsInBounds(objects: SceneObject[], bounds: Bounds): SceneObject[] {
  const bx2 = bounds.x + bounds.w;
  const by2 = bounds.y + bounds.h;
  return objects.filter((obj) => {
    const w = typeof obj.w === "number" ? obj.w : 0;
    const h = typeof obj.h === "number" ? obj.h : 0;
    const ox2 = obj.x + w;
    const oy2 = obj.y + h;
    return obj.x < bx2 && ox2 > bounds.x && obj.y < by2 && oy2 > bounds.y;
  });
}

/** Render scene region to PNG blob (SVG → canvas). */
export async function exportBoardPng(opts: ExportBoardPngOptions): Promise<Blob> {
  const scale = opts.scale ?? DEFAULT_SCALE;
  const background = opts.background ?? DEFAULT_BG;
  const credentials = opts.credentials ?? "include";
  const pad = EXPORT_PADDING;
  const viewX = opts.bounds.x - pad;
  const viewY = opts.bounds.y - pad;
  const viewW = Math.max(1, opts.bounds.w + pad * 2);
  const viewH = Math.max(1, opts.bounds.h + pad * 2);

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("xmlns:xlink", XLINK_NS);
  svg.setAttribute("width", String(Math.ceil(viewW * scale)));
  svg.setAttribute("height", String(Math.ceil(viewH * scale)));
  svg.setAttribute("viewBox", `${viewX} ${viewY} ${viewW} ${viewH}`);

  const bg = document.createElementNS(SVG_NS, "rect");
  bg.setAttribute("x", String(viewX));
  bg.setAttribute("y", String(viewY));
  bg.setAttribute("width", String(viewW));
  bg.setAttribute("height", String(viewH));
  bg.setAttribute("fill", background);
  svg.appendChild(bg);

  const content = document.createElementNS(SVG_NS, "g");
  svg.appendChild(content);

  const visible = filterObjectsInBounds(opts.objects, {
    x: viewX,
    y: viewY,
    w: viewW,
    h: viewH,
  });

  const tempLayer = document.createElementNS(SVG_NS, "svg");
  renderSvgSceneLayer(tempLayer, visible, {
    assetContentUrl: opts.assetContentUrl,
    canEdit: false,
  });
  const defs = tempLayer.querySelector("defs");
  if (defs) svg.insertBefore(defs.cloneNode(true), content);
  for (const child of [...tempLayer.childNodes]) {
    if (child.nodeName === "defs") continue;
    content.appendChild(child);
  }

  await sanitizeSvgForExport(svg, credentials);

  const serialized = new XMLSerializer().serializeToString(svg);
  const url = URL.createObjectURL(
    new Blob([serialized], { type: "image/svg+xml;charset=utf-8" })
  );

  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewW * scale);
    canvas.height = Math.ceil(viewH * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    if (!blob) {
      throw new Error(
        "Не удалось сохранить изображение (возможно, на рамке есть внешние картинки без доступа)"
      );
    }
    return blob;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/taint|Tainted|SecurityError/i.test(msg)) {
      throw new Error(
        "Не удалось сохранить: часть изображений недоступна для экспорта. Попробуйте ещё раз или уберите внешние картинки с рамки."
      );
    }
    throw e;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportFilename(boardTitle: string): string {
  const safe = boardTitle
    .trim()
    .replace(/[^\w\u0400-\u04FF.-]+/gi, "-")
    .replace(/-+/g, "-")
    .slice(0, 64);
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `${safe || "board"}-${stamp}.png`;
}

export function boundsForViewport(
  viewportRect: DOMRect,
  panX: number,
  panY: number,
  scale: number
): Bounds {
  const s = scale > 0 ? scale : 1;
  return {
    x: -panX / s,
    y: -panY / s,
    w: viewportRect.width / s,
    h: viewportRect.height / s,
  };
}

export function boundsForSelection(
  objects: SceneObject[],
  selectedIds: string[]
): Bounds | null {
  const selected = objects.filter((o) => selectedIds.includes(o.id));
  return unionBounds(selected);
}
